import { Component, inject, OnInit, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { AccountsFacadeService } from '@dgacontrol-frontend/accounts-data-access';
import {
  extractErrorMessage,
  HTTP_ERROR_OPTIONS,
} from '@dgacontrol-frontend/core';
import { FrmLoginComponent } from '@dgacontrol-frontend/shared/ui/auth-ui';
import { MESSAGE_KEYS } from '@dgacontrol-frontend/shared/constants';

// Why the previous session ended, as `AuthService.logout()` spells it in `?reason=`.
// The eviction happens elsewhere and this is the screen the user lands on, so it is the
// only place left that can explain it. Narrower than the `web` map on purpose: admin has
// no self-service password change, so `password_changed` never reaches it. Reasons absent
// from the map say nothing ON PURPOSE — `manual` needs no explanation (the user clicked
// sign out), and the guard passes no reason at all, because it cannot tell a dead session
// from a visitor who never had one.
// `| undefined` is not noise: the key comes from a user-editable query string, so a miss
// is the normal case, not an edge one.
const EVICTION_NOTICE: Record<string, string | undefined> = {
  expired: 'Su sesión expiró. Inicie sesión nuevamente.',
};

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [FrmLoginComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  messageService: MessageService = inject(MessageService);
  accountsFacade: AccountsFacadeService = inject(AccountsFacadeService);
  private router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly errorOptions =
    inject(HTTP_ERROR_OPTIONS, { optional: true }) ?? {};
  readonly coreLoginComponent = viewChild.required<FrmLoginComponent>('vcCoreLoginComponent');

  ngOnInit(): void {
    const detail =
      EVICTION_NOTICE[this.route.snapshot.queryParamMap.get('reason') ?? ''];
    if (detail === undefined) return;
    this.messageService.add({
      key: MESSAGE_KEYS.global,
      severity: 'info',
      summary: 'Sesión finalizada',
      detail,
      life: 8000,
    });
  }

  onSubmit(event: { username: string; password: string }) {
    // Username trimmed (the backend rejects whitespace-only text); the password is sent
    // exactly as typed — spaces can be part of it.
    const data = {
      username: event.username.trim(),
      password: event.password,
      must_be_superuser: true,
    };
    this.coreLoginComponent().customProgressSpinner().setText('Ingresando');
    this.coreLoginComponent().customProgressSpinner().showSpinner();
    this.accountsFacade.login(data).subscribe({
      next: (res) => {
        this.coreLoginComponent().customProgressSpinner().hideSpinner();
        localStorage.setItem('token_jwt', res.token_jwt);
        localStorage.setItem('username', event.username);
        void this.router.navigate(['/users']);
      },
      error: (err: HttpErrorResponse) => {
        this.coreLoginComponent().customProgressSpinner().hideSpinner();
        this.messageService.add({
          key: MESSAGE_KEYS.global,
          severity: 'error',
          summary: 'Error al iniciar sesión',
          // `/login` is carved out of httpErrorInterceptor (this component owns its
          // toast), so the shared extractor has to be called by hand — otherwise the
          // app-level copy policy in HTTP_ERROR_OPTIONS never reaches the login screen.
          detail: extractErrorMessage(err, this.errorOptions),
        });
      },
    });
  }
}
